import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Fade,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import {
  FaFileExcel,
  FaFilePdf,
  FaTimes,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaUserTie,
  FaUsers,
  FaUserPlus,
  FaIdCard,
  FaBell,
  FaConciergeBell,
  FaBoxOpen,
  FaBoxes,
  FaTicketAlt,
  FaStoreAlt,
  FaUserShield,
  FaFileAlt,
  FaClipboardList,
  FaChevronLeft,
  FaLayerGroup,
  FaGripVertical,
  FaChevronDown,
  FaChevronUp,
} from 'react-icons/fa';
import { useMain } from 'src/views/hooks/useMain';

/* Icon + accent color per report model key. Falls back to a generic icon. */
const MODEL_META = {
  billing: { icon: FaFileInvoiceDollar, color: '#6366F1' },
  dueBill: { icon: FaMoneyBillWave, color: '#F59E0B' },
  packageBill: { icon: FaBoxes, color: '#8B5CF6' },
  draftBill: { icon: FaFileAlt, color: '#64748B' },
  expense: { icon: FaMoneyBillWave, color: '#EF4444' },
  appointment: { icon: FaCalendarAlt, color: '#0EA5E9' },
  staff: { icon: FaUserTie, color: '#10B981' },
  client: { icon: FaUsers, color: '#3B82F6' },
  clientEnquiry: { icon: FaUserPlus, color: '#14B8A6' },
  // membership: { icon: FaIdCard, color: '#A855F7' },
  serviceReminder: { icon: FaBell, color: '#F97316' },
  services: { icon: FaConciergeBell, color: '#EC4899' },
  product: { icon: FaBoxOpen, color: '#22C55E' },
  consumeableProduct: { icon: FaBoxes, color: '#84CC16' },
  coupon: { icon: FaTicketAlt, color: '#F43F5E' },
  branch: { icon: FaStoreAlt, color: '#0284C7' },
  salonOwner: { icon: FaUserShield, color: '#7C3AED' },
};
const DEFAULT_META = { icon: FaClipboardList, color: '#64748B' };
const COMBINED_META = { icon: FaLayerGroup, color: '#4338CA' };

const ExportReportDialog = ({ open, onClose, branchId }) => {
  const { exportReport, exportCombinedReport, getAvailableReportModels, setShowMessage } = useMain();

  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // step: 1 = pick model, 2 = single-report configure + export, 3 = combined configure + export
  const [step, setStep] = useState(1);
  const [selectedModel, setSelectedModel] = useState('');
  const [billSubType, setBillSubType] = useState('all');
  const [format, setFormat] = useState('excel');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  // ---- combined export state ----
  const [combinedModels, setCombinedModels] = useState([]); // [{ key, label, columns: [] }]
  const [expandedCombinedKey, setExpandedCombinedKey] = useState('');
  const [dragOverZone, setDragOverZone] = useState(false);
  const dragDataRef = React.useRef(null); // { source: 'available' | 'selected', key, index }

  const getColumnsForModel = (config, subType) => {
    if (!config?.columns) return [];
    if (Array.isArray(config.columns)) return [...config.columns];
    return config.columns[subType] ? [...config.columns[subType]] : [];
  };
  const selectedModelConfig = models.find((m) => m.key === selectedModel);
  const activeColumns = getColumnsForModel(selectedModelConfig, billSubType);

  useEffect(() => {
    if (!open) return;
    const fetchModels = async () => {
      setLoadingModels(true);
      try {
        const res = await getAvailableReportModels();
        setModels(res?.data || []);
      } catch (err) {
        setShowMessage?.({ message: 'Failed to load report types', messageType: 'error' });
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, [open]);
  useEffect(() => {
    if (selectedModel === 'billing') {
      setSelectedColumns(getColumnsForModel(selectedModelConfig, billSubType));
    }
  }, [billSubType]);
  const resetForm = () => {
    setStep(1);
    setSelectedModel('');
    setBillSubType('all');
    setFormat('excel');
    setFromDate('');
    setToDate('');
    setSelectedColumns([]);
    setCombinedModels([]);
    setExpandedCombinedKey('');
  };

  const toggleColumn = (col) => {
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col],
    );
  };

  const toggleAllColumns = () => {
    const allCols = getColumnsForModel(selectedModelConfig, billSubType);
    if (selectedColumns.length === allCols.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(allCols);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

const handlePickModel = (key) => {
  setSelectedModel(key);
  setBillSubType('all');
  const config = models.find((m) => m.key === key);
  const cols = getColumnsForModel(config, 'all'); // resolves object-vs-array case
  setSelectedColumns(cols);
  setStep(2);
};



  const handlePickCombined = () => {
    setCombinedModels([]);
    // setExpandedCombinedKey('');
    setStep(3);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const { blob, filename } = await exportReport(
        branchId,
        selectedModel,
        format,
        fromDate || undefined,
        toDate || undefined,
        selectedColumns.length ? selectedColumns.join(',') : undefined,
        selectedModel === 'billing' ? billSubType : undefined,
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setShowMessage?.({ message: 'Report downloaded successfully', messageType: 'success' });
      handleClose();
    } catch (err) {
      setShowMessage?.({ message: err.message || 'Failed to export report', messageType: 'error' });
    } finally {
      setExporting(false);
    }
  };

  // ---- combined export helpers ----

  // const availableForCombined = useMemo(
  //   () => models.filter((m) => !combinedModels.some((cm) => cm.key === m.key)),
  //   [models, combinedModels],
  // );

  // const addModelToCombined = (model) => {
  //   setCombinedModels((prev) => {
  //     if (prev.some((cm) => cm.key === model.key)) return prev;
  //     return [
  //       ...prev,
  //       {
  //         key: model.key,
  //         label: model.label,
  //         columns: model.columns ? [...model.columns] : [],
  //         allColumns: model.columns ? [...model.columns] : [],
  //       },
  //     ];
  //   });
  //   setExpandedCombinedKey(model.key);
  // };

  // const removeModelFromCombined = (key) => {
  //   setCombinedModels((prev) => prev.filter((cm) => cm.key !== key));
  //   if (expandedCombinedKey === key) setExpandedCombinedKey('');
  // };

  // const reorderCombined = (fromIndex, toIndex) => {
  //   setCombinedModels((prev) => {
  //     const next = [...prev];
  //     const [moved] = next.splice(fromIndex, 1);
  //     next.splice(toIndex, 0, moved);
  //     return next;
  //   });
  // };

  // const toggleCombinedColumn = (modelKey, col) => {
  //   setCombinedModels((prev) =>
  //     prev.map((cm) =>
  //       cm.key === modelKey
  //         ? {
  //             ...cm,
  //             columns: cm.columns.includes(col)
  //               ? cm.columns.filter((c) => c !== col)
  //               : [...cm.columns, col],
  //           }
  //         : cm,
  //     ),
  //   );
  // };

  // const toggleAllCombinedColumns = (modelKey) => {
  //   setCombinedModels((prev) =>
  //     prev.map((cm) =>
  //       cm.key === modelKey
  //         ? { ...cm, columns: cm.columns.length === cm.allColumns.length ? [] : [...cm.allColumns] }
  //         : cm,
  //     ),
  //   );
  // };

  // // Available-list item drag start
  // const handleAvailableDragStart = (model) => {
  //   dragDataRef.current = { source: 'available', model };
  // };

  // // Selected-list item drag start (for reordering)
  // const handleSelectedDragStart = (index) => {
  //   dragDataRef.current = { source: 'selected', index };
  // };

  // const handleDropZoneDragOver = (e) => {
  //   e.preventDefault();
  //   setDragOverZone(true);
  // };

  // const handleDropZoneDragLeave = () => setDragOverZone(false);

  // const handleDropZoneDrop = (e) => {
  //   e.preventDefault();
  //   setDragOverZone(false);
  //   const data = dragDataRef.current;
  //   if (!data) return;
  //   if (data.source === 'available') {
  //     addModelToCombined(data.model);
  //   }
  //   dragDataRef.current = null;
  // };

  // const handleSelectedItemDrop = (targetIndex) => (e) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   setDragOverZone(false);
  //   const data = dragDataRef.current;
  //   if (!data) return;
  //   if (data.source === 'available') {
  //     addModelToCombined(data.model);
  //   } else if (data.source === 'selected' && data.index !== targetIndex) {
  //     reorderCombined(data.index, targetIndex);
  //   }
  //   dragDataRef.current = null;
  // };
  const isInCombined = (key) => combinedModels.some((cm) => cm.key === key);

const toggleModelInCombined = (model) => {
  setCombinedModels((prev) => {
    if (prev.some((cm) => cm.key === model.key)) {
      if (expandedCombinedKey === model.key) setExpandedCombinedKey('');
      return prev.filter((cm) => cm.key !== model.key);
    }
        // For billing (object columns) use the "all" sub-type; other models are plain arrays
    const cols = getColumnsForModel(model, 'all');
    return [
      ...prev,
      {
        key: model.key,
        label: model.label,
        columns: [...cols],
        allColumns: [...cols],
      },
    ];
  });
};

const moveCombined = (index, direction) => {
  setCombinedModels((prev) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= prev.length) return prev;
    const next = [...prev];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    return next;
  });
};

const toggleCombinedColumn = (modelKey, col) => {
  setCombinedModels((prev) =>
    prev.map((cm) =>
      cm.key === modelKey
        ? {
            ...cm,
            columns: cm.columns.includes(col)
              ? cm.columns.filter((c) => c !== col)
              : [...cm.columns, col],
          }
        : cm,
    ),
  );
};

const toggleAllCombinedColumns = (modelKey) => {
  setCombinedModels((prev) =>
    prev.map((cm) =>
      cm.key === modelKey
        ? { ...cm, columns: cm.columns.length === cm.allColumns.length ? [] : [...cm.allColumns] }
        : cm,
    ),
  );
};

  const handleExportCombined = async () => {
  if (!combinedModels.length) {
    setShowMessage?.({ message: 'Add at least one report to combine', messageType: 'error' });
    return;
  }
  try {
    setExporting(true);
    const reportsPayload = combinedModels.map((cm) => ({
      model: cm.key,                                   // was: key: cm.key
      fields: cm.columns.length ? cm.columns : undefined, // was: columns: cm.columns
    }));

    const { blob, filename } = await exportCombinedReport(
      format,
      branchId,
      fromDate || undefined,
      toDate || undefined,
      reportsPayload,
    );

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setShowMessage?.({ message: 'Combined report downloaded successfully', messageType: 'success' });
    handleClose();
  } catch (err) {
    setShowMessage?.({ message: err.message || 'Failed to export combined report', messageType: 'error' });
  } finally {
    setExporting(false);
  }
};
  const rangeLabel = useMemo(() => {
    if (!fromDate && !toDate) return 'All time';
    return `${fromDate || 'Start'} → ${toDate || 'Today'}`;
  }, [fromDate, toDate]);

  const dialogMaxWidth = step === 3 ? 'md' : 'sm';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={dialogMaxWidth}
      PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden', transition: 'max-width 0.2s ease' } }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #6274DD 0%, #6274DD 100%)',
          color: '#fff',
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {(step === 2 || step === 3) && (
            <IconButton size="small" onClick={() => setStep(1)} sx={{ color: '#fff', opacity: 0.85 }}>
              <FaChevronLeft size={14} />
            </IconButton>
          )}
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              Export Report
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }}>
              {step === 1 && 'Choose a report to export'}
              {step === 2 && selectedModelConfig?.label}
              {step === 3 && 'Combine multiple reports into one file'}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: '#fff' }}>
          <FaTimes size={14} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, bgcolor: '#F8FAFC' }}>
        {/* ---------------- STEP 1: MODEL PICKER ---------------- */}
        {step === 1 && (
          <Fade in={step === 1}>
            <Box sx={{ p: 3 }}>
              {loadingModels ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <>
                  {/* Combined report entry point */}
                  <Box
                    onClick={handlePickCombined}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: '14px',
                      border: '1.5px dashed',
                      borderColor: COMBINED_META.color,
                      bgcolor: `${COMBINED_META.color}0D`,
                      p: 1.75,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        boxShadow: `0 4px 14px ${COMBINED_META.color}22`,
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        flexShrink: 0,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${COMBINED_META.color}18`,
                        color: COMBINED_META.color,
                      }}
                    >
                      <FaLayerGroup size={16} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700}>
                        Combined Report
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Merge multiple reports into a single file
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 1.5,
                      maxHeight: 380,
                      overflowY: 'auto',
                      pr: 0.5,
                    }}
                  >
                    {models.map((m) => {
                      const meta = MODEL_META[m.key] || DEFAULT_META;
                      const Icon = meta.icon;
                      return (
                        <Box
                          key={m.key}
                          onClick={() => handlePickModel(m.key)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: '14px',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#fff',
                            p: 1.75,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: meta.color,
                              boxShadow: `0 4px 14px ${meta.color}22`,
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              flexShrink: 0,
                              borderRadius: '10px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: `${meta.color}18`,
                              color: meta.color,
                            }}
                          >
                            <Icon size={16} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {m.label}
                            </Typography>
                            {m.subTypes && (
                              <Typography variant="caption" color="text.secondary">
                                {m.subTypes.length} bill types
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
            </Box>
          </Fade>
        )}

        {/* ---------------- STEP 2: SINGLE REPORT CONFIGURE + EXPORT ---------------- */}
        {step === 2 && selectedModelConfig && (
          <Fade in={step === 2}>
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Bill sub-type chips (billing model only) */}
              {selectedModelConfig.subTypes && (
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Bill Type
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {selectedModelConfig.subTypes.map((st) => (
                      <Chip
                        key={st.key}
                        label={st.label}
                        onClick={() => setBillSubType(st.key)}
                        sx={{
                          fontWeight: 600,
                          borderRadius: '8px',
                          bgcolor: billSubType === st.key ? '#4338CA' : '#fff',
                          color: billSubType === st.key ? '#fff' : '#334155',
                          border: '1px solid',
                          borderColor: billSubType === st.key ? '#4338CA' : '#E2E8F0',
                          '&:hover': {
                            bgcolor: billSubType === st.key ? '#3730A3' : '#F1F5F9',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Date range */}
              {selectedModelConfig.supportsDateFilter && (
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Duration
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                    <TextField
                      label="From"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      sx={{ bgcolor: '#fff', borderRadius: '8px' }}
                    />
                    <TextField
                      label="To"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      sx={{ bgcolor: '#fff', borderRadius: '8px' }}
                    />
                  </Box>
                </Box>
              )}
              {!selectedModelConfig.supportsDateFilter && (
                <Typography variant="caption" color="text.secondary">
                  This report exports all available records — no date filter applies.
                </Typography>
              )}
              {/* Column selection */}
              {activeColumns.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Columns ({selectedColumns.length}/{activeColumns.length})
                    </Typography>
                    <Button size="small" onClick={toggleAllColumns} sx={{ textTransform: 'none', fontWeight: 600 }}>
                      {selectedColumns.length === activeColumns.length ? 'Clear All' : 'Select All'}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: 180,
                      overflowY: 'auto',
                      border: '1px solid #E2E8F0',
                      borderRadius: '10px',
                      bgcolor: '#fff',
                      p: 1,
                      mt: 1,
                    }}
                  >
                    <FormGroup>
    {activeColumns.map((col) => (
                        <FormControlLabel
                          key={col}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedColumns.includes(col)}
                              onChange={() => toggleColumn(col)}
                            />
                          }
                          label={<Typography variant="body2">{col}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </Box>
              )}

              {/* Format selection */}
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Export Format
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                  {[
                    { key: 'excel', label: 'Excel', sub: '.xlsx spreadsheet', icon: FaFileExcel, color: '#16A34A' },
                    { key: 'pdf', label: 'PDF', sub: 'Printable document', icon: FaFilePdf, color: '#DC2626' },
                  ].map((f) => {
                    const Icon = f.icon;
                    const active = format === f.key;
                    return (
                      <Box
                        key={f.key}
                        onClick={() => setFormat(f.key)}
                        sx={{
                          flex: 1,
                          cursor: 'pointer',
                          borderRadius: '12px',
                          border: '2px solid',
                          borderColor: active ? f.color : '#E2E8F0',
                          bgcolor: active ? `${f.color}0D` : '#fff',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <Icon size={22} color={f.color} />
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {f.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {f.sub}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Divider />

              {/* Summary strip */}
              <Box
                sx={{
                  bgcolor: '#EEF2FF',
                  border: '1px solid #C7D2FE',
                  borderRadius: '10px',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="caption" fontWeight={700} color="#4338CA">
                  SUMMARY
                </Typography>
                <Typography variant="body2" color="#3730A3">
                  {selectedModelConfig.label}
                  {selectedModelConfig.subTypes
                    ? ` • ${selectedModelConfig.subTypes.find((s) => s.key === billSubType)?.label}`
                    : ''}
                  {' • '}
                  {rangeLabel}
                  {' • '}
                  {format.toUpperCase()}
                </Typography>
              </Box>

              {/* Actions */}
             <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleClose}
                  disabled={exporting}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleExport}
                  disabled={exporting}
                  startIcon={
                    exporting ? (
                      <CircularProgress
                        size={16}
                        sx={{ color: '#fff' }}
                      />
                    ) : null
                  }
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    px: 3,
                    backgroundColor: '#6274DD',
                    color: '#fff',

                    '&:hover': {
                      backgroundColor: '#5567D1',
                    },

                    '&:disabled': {
                      backgroundColor: '#6274DD',
                      color: '#fff',
                      opacity: 0.8,
                    },
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export Report'}
                </Button>
              </Box>
            </Box>
          </Fade>
        )}

        {/* ---------------- STEP 3: COMBINED REPORT (DRAG & DROP) ---------------- */}
        {step === 3 && (
          <Fade in={step === 3}>
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  1 · Pick reports to combine
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1,
                    maxHeight: 190,
                    overflowY: 'auto',
                    pr: 0.5,
                  }}
                >
                  {loadingModels ? (
                    <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : (
                    models.map((m) => {
                      const meta = MODEL_META[m.key] || DEFAULT_META;
                      const Icon = meta.icon;
                      const selected = isInCombined(m.key);
                      const order = combinedModels.findIndex((cm) => cm.key === m.key) + 1;
                      return (
                        <Box
                          key={m.key}
                          onClick={() => toggleModelInCombined(m)}
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: '12px',
                            border: '1.5px solid',
                            borderColor: selected ? meta.color : '#E2E8F0',
                            bgcolor: selected ? `${meta.color}12` : '#fff',
                            p: 1.25,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: 0.5,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              borderColor: meta.color,
                              boxShadow: `0 3px 10px ${meta.color}22`,
                            },
                          }}
                        >
                          {selected && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                bgcolor: meta.color,
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {order}
                            </Box>
                          )}
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '9px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: `${meta.color}18`,
                              color: meta.color,
                            }}
                          >
                            <Icon size={14} />
                          </Box>
                          <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: '100%' }}>
                            {m.label}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Tap a report to add it. The number shows the order it'll appear in the file.
                </Typography>
              </Box>

              {/* Selected reports — simple numbered list, arrows to reorder */}
              {combinedModels.length > 0 && (
                <Box>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    2 · Order & customize ({combinedModels.length} selected)
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      maxHeight: 240,
                      overflowY: 'auto',
                      pr: 0.5,
                    }}
                  >
                    {combinedModels.map((cm, index) => {
                      const meta = MODEL_META[cm.key] || DEFAULT_META;
                      const Icon = meta.icon;
                      const expanded = expandedCombinedKey === cm.key;
                      return (
                        <Box
                          key={cm.key}
                          sx={{
                            borderRadius: '10px',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#F8FAFC',
                            overflow: 'hidden',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.85 }}>
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: meta.color,
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {index + 1}
                            </Box>
                            <Box sx={{ color: meta.color, display: 'flex' }}>
                              <Icon size={13} />
                            </Box>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                              {cm.label}
                            </Typography>

                            {/* Reorder arrows */}
                            <IconButton size="small" disabled={index === 0} onClick={() => moveCombined(index, -1)}>
                              <FaChevronUp size={11} />
                            </IconButton>
                            <IconButton
                              size="small"
                              disabled={index === combinedModels.length - 1}
                              onClick={() => moveCombined(index, 1)}
                            >
                              <FaChevronDown size={11} />
                            </IconButton>

                            {cm.allColumns.length > 0 && (
                              <>
                                <Typography variant="caption" color="text.secondary" sx={{ mx: 0.25 }}>
                                  {cm.columns.length}/{cm.allColumns.length} cols
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={() => setExpandedCombinedKey(expanded ? '' : cm.key)}
                                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, minWidth: 0, px: 1 }}
                                >
                                  {expanded ? 'Hide' : 'Edit'}
                                </Button>
                              </>
                            )}
                            <IconButton size="small" onClick={() => toggleModelInCombined(cm)}>
                              <FaTimes size={11} />
                            </IconButton>
                          </Box>

                          {expanded && cm.allColumns.length > 0 && (
                            <Box sx={{ borderTop: '1px solid #E2E8F0', bgcolor: '#fff', p: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  onClick={() => toggleAllCombinedColumns(cm.key)}
                                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12 }}
                                >
                                  {cm.columns.length === cm.allColumns.length ? 'Clear all' : 'Select all'}
                                </Button>
                              </Box>
                              <Box sx={{ maxHeight: 160, overflowY: 'auto', pr: 0.5 }}>
                                <FormGroup>
                                  {cm.allColumns.map((col) => (
                                    <FormControlLabel
                                      key={col}
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={cm.columns.includes(col)}
                                          onChange={() => toggleCombinedColumn(cm.key, col)}
                                        />
                                      }
                                      label={<Typography variant="body2">{col}</Typography>}
                                    />
                                  ))}
                                </FormGroup>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
              {/* Duration */}
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Duration
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                  <TextField
                    label="From"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    sx={{ bgcolor: '#fff', borderRadius: '8px' }}
                  />
                  <TextField
                    label="To"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    sx={{ bgcolor: '#fff', borderRadius: '8px' }}
                  />
                </Box>
              </Box>

              {/* Format */}
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Export Format
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                  {[
                    { key: 'excel', label: 'Excel', sub: '.xlsx spreadsheet', icon: FaFileExcel, color: '#16A34A' },
                    { key: 'pdf', label: 'PDF', sub: 'Printable document', icon: FaFilePdf, color: '#DC2626' },
                  ].map((f) => {
                    const Icon = f.icon;
                    const active = format === f.key;
                    return (
                      <Box
                        key={f.key}
                        onClick={() => setFormat(f.key)}
                        sx={{
                          flex: 1,
                          cursor: 'pointer',
                          borderRadius: '12px',
                          border: '2px solid',
                          borderColor: active ? f.color : '#E2E8F0',
                          bgcolor: active ? `${f.color}0D` : '#fff',
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <Icon size={22} color={f.color} />
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {f.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {f.sub}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Divider />

              {/* Summary */}
              <Box
                sx={{
                  bgcolor: '#EEF2FF',
                  border: '1px solid #C7D2FE',
                  borderRadius: '10px',
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="caption" fontWeight={700} color="#4338CA">
                  SUMMARY
                </Typography>
                <Typography variant="body2" color="#3730A3">
                  {combinedModels.length
                    ? combinedModels.map((cm) => cm.label).join(' + ')
                    : 'No reports selected'}
                  {' • '}
                  {rangeLabel}
                  {' • '}
                  {format.toUpperCase()}
                </Typography>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleClose}
                  disabled={exporting}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant="contained"
                  onClick={handleExportCombined}
                  disabled={exporting || combinedModels.length === 0}
                  startIcon={
                    exporting ? (
                      <CircularProgress
                        size={16}
                        sx={{ color: '#fff' }}
                      />
                    ) : null
                  }
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    px: 3,
                    backgroundColor: '#6274DD',
                    color: '#fff',

                    '&:hover': {
                      backgroundColor: '#5567D1',
                    },

                    '&:disabled': {
                      backgroundColor: '#6274DD',
                      color: '#fff',
                      opacity: 0.8,
                    },
                  }}
                >
                  {exporting ? 'Exporting...' : 'Generate Combined Report'}
                </Button>
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExportReportDialog;